import { TBreadCrumb } from './Breadcrumb.types';
import type {
  JsonApiDocument,
  JsonApiListResponse,
  JsonApiSingleResponse,
} from './JsonApi.types';
import type { IRelationships, RelationType } from './Relationships.types';

export interface ProductAttr extends JsonApiDocument {
  type: string;
  id: string;
  attributes: {
    dimensions: string;
    name: string;
    description: string;
    available_on: string;
    slug: string;
    meta_description: string | null;
    meta_keywords: string | null;
    updated_at: string;
    sku: string;
    purchasable: boolean;
    backorderable: boolean;
    available: boolean;
    currency: string;
    price: string;
    display_price: string;
    quantity_limit: number[];
    bulk_discount: {
      promo_title: string;
      promo_description: string;
    };
    breadcrumbs: TBreadCrumb[];
    ingredients: string;
    return_policy: string;
    disclaimer_text: string;
    instructions: string;
    in_stock: boolean;
    is_having_bulk_discount: boolean;
    is_having_subscription_adjustments: boolean;
    language_based_variants: LanguageBasedVariant[];
    assets: ProductAsset[];
    material_type: string;
    is_customizable: boolean;
    discontinue_on: string;
    product_group?: ProductGroupResponse;
  };
  relationships: IRelationships & {
    default_variant: {
      data: RelationType;
    };
  };
}

export interface LanguageBasedVariant {
  language: string;
  sku: string;
  variantId: string;
  quantity_limit: number[];
}

export interface ProductAsset {
  title: string;
  url: string;
}

export interface IProduct extends JsonApiSingleResponse {
  data: ProductAttr;
}

export interface IProducts extends JsonApiListResponse {
  data: ProductAttr[];
}

export interface Product {
  id: string;
  variantId: string;
  slug: string;
  name: string;
  sku: string;
  price: number;
  displayPrice: string;
  description?: string;
  images: {
    id?: string;
    original: string;
    thumbnail: string;
  }[];
  bulkDiscount: {
    title: string;
    description: string;
  };
  quantityLimit: number[];
  inStock: boolean;
  breadcrumb: TBreadCrumb[];
  ingredients?: string;
  returnPolicy?: string;
  disclaimerText?: string;
  instructions?: string;
  isHavingBulkDiscount: boolean;
  languageBasedVariants: LanguageBasedVariant[];
  assets: ProductAsset[];
  materialType: string;
  isCustomizable: boolean;
  discontinueOn: string;
  dimensions: string;
  AvailableOn: string;
  productGroup?: ProductGroup;
}

export interface FlashSaleResponse {
  message: string;
  expires_at_utc: string;
}

export interface FlashSaleClient {
  message: string;
  expiresAtUTC: string;
}

export interface RedeemProductResponse {
  id: number;
  sku: string;
  points: string;
  redeem_price: string;
  amount_payable_after_redeem_points: string;
  display_price_with_points: string;
}

export interface RedeemProductClient {
  id: number;
  sku: string;
  points: string;
  price: string;
  displayPriceWithoutPoints: string;
  displayPriceWithPoints: string;
}

export interface PromotionListResponse {
  data: {
    promotional_products: ProductDiscounts[];
    flashsale: FlashSaleResponse;
    redeem_products: RedeemProductResponse[];
  };
}

export interface ProductDiscounts {
  sku: string;
  discounts: Discount[];
}

export type Discount = BulkDiscount | CouponDiscount | DSODiscount;

export interface BulkDiscount {
  discount_type: 'bulk_discount';
  title: string;
  description: string;
}

export interface DSODiscount {
  discount_type: 'dso_discount';
  title: string;
  description: string;
  discount_percentage: number;
}

export interface CouponDiscount {
  discount_type: 'coupon';
  highlighter_text: string;
  code: string;
  match_policy: string;
  description: string;
  name: string;
  type: string;
  is_flashsale: boolean;
  usage: number;
  expires_at_utc: string;
  original_price: string;
  discount_price: string;
  stackable: boolean;
}

export interface PromotionListClient {
  productsList: ProductDiscountsClient[];
  flashSale: FlashSaleClient;
  redeemProductList: RedeemProductClient[];
}

export interface ProductDiscountsClient {
  sku: string;
  discounts: DiscountClient[];
}

export type DiscountClient =
  | BulkDiscountClient
  | CouponClient
  | DSODiscountClient;

export interface BulkDiscountClient {
  discountType: 'bulk_discount';
  title: string;
  description: string;
}

export interface DSODiscountClient {
  discountType: 'dso_discount';
  title: string;
  description: string;
  discountPercentage: number;
}

export interface CouponClient {
  discountType: 'coupon';
  highlighterText: string;
  code: string;
  matchPolicy: string;
  description: string;
  name: string;
  type: string;
  isFlashSale: boolean;
  usage: number;
  expiresAt: string;
  originalPrice: string;
  discountPrice: string;
  stackable: boolean;
}

export enum MaterialTypeEnum {
  physical = 'physical',
  digital = 'digital',
  digital_physical = 'digital_physical',
}

export interface ProductsRequestParams {
  taxonSlug?: string;
  sortDirection?: string;
  variantIds?: string[];
  skus?: string[];
  categoryFilters?: string[];
  pageSize?: number;
}

// --- Product Group (variant selector) ---

export interface ProductGroupOptionType {
  id: number;
  name: string;
  presentation: string;
}

export interface ProductGroupOptionValue {
  optionTypeId: number;
  optionTypeName: string;
  id: number;
  name: string;
  presentation: string;
}

export interface ProductGroupMember {
  productId: number;
  variantId: string;
  name: string;
  slug: string;
  sku: string;
  current: boolean;
  position: number;
  optionValues: ProductGroupOptionValue[];
  imageUrl: string;
  allowedCountries?: string[];
}

export interface ProductGroup {
  id: number;
  name: string;
  optionTypes: ProductGroupOptionType[];
  members: ProductGroupMember[];
}

// Server response format (snake_case) for product_group
export interface ProductGroupResponse {
  id: number;
  name: string;
  option_types: {
    id: number;
    name: string;
    presentation: string;
  }[];
  members: {
    product_id: number;
    variant_id: string;
    name: string;
    slug: string;
    sku: string;
    current: boolean;
    position: number;
    option_values: {
      option_type_id: number;
      option_type_name: string;
      id: number;
      name: string;
      presentation: string;
    }[];
    image_url: string;
    allowed_countries?: string[];
  }[];
}
